/* eslint-disable security/detect-object-injection */
/* eslint-disable sort-exports/sort-exports */
import React from 'react';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ManagementDashboardToggle from 'components/ManagementToggle';
import Spacer from 'components/Spacer';
import { UserPermissions } from 'global-constants';
import { usePermissionRedirect } from 'hooks/usePermissionRedirect';
import useGetUserPermissions from 'hooks/useGetUserPermissions';
import * as ShippingStyles from './styles';
import * as ShippingConstants from './constants';
import { Button } from '@mui/material';
import ShippingSearch from './components/ShippingSearch';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UndoIcon from '@mui/icons-material/Undo';
import ShippingTable from './components/ShippingTable';
import EditIcon from '@mui/icons-material/Edit';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedOrderItemIds } from 'store/slices/shipping/selectedOrderItems';
import {
  selectOrderItemChanges,
  clearOrderItemChanges,
  revertToOriginal,
  selectOriginalOrderItems,
  storeOriginalOrderItems,
} from 'store/slices/shipping/orderItemChanges';

import EditOrderItemsModal from './components/EditOrderItemsModal';
import { useNotification } from '../../hooks/useNotification';
import { NewOrderItem } from 'types';
import { MonoService } from 'api/rest/config';
import { useShipmentCarriers } from './hooks/useShipmentCarriers';
import { getShipmentInfo } from './utils';

const Shipping = () => {
  const userPermissions = useGetUserPermissions();
  usePermissionRedirect(
    userPermissions,
    UserPermissions.ASSET__MANAGEMENT__VIEW
  );

  const styles = ShippingStyles;
  const fixed = ShippingConstants;
  const dispatch = useDispatch();
  const { showSuccess, showError } = useNotification();

  // Get selected order item IDs and changes from Redux store
  const selectedOrderItemIds = useSelector(selectSelectedOrderItemIds);
  const orderItemChanges = useSelector(selectOrderItemChanges);
  const originalOrderItems = useSelector(selectOriginalOrderItems);

  // State for edit modal
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  // State to store selected order items data
  const [selectedOrderItems, setSelectedOrderItems] = React.useState<
    NewOrderItem[]
  >([]);

  // State to store the selected order ID
  const [selectedOrderId, setSelectedOrderId] = React.useState<string | null>(
    null
  );

  // Get shipment carriers
  const { carriers, loading: loadingCarriers } = useShipmentCarriers();

  // Ref to store the refetch function from ShippingTable
  const refetchOrderItemsRef = React.useRef<() => Promise<any>>(() =>
    Promise.resolve()
  );

  // Function to set the refetch function
  const setRefetchOrderItems = React.useCallback(
    (refetch: () => Promise<any>) => {
      refetchOrderItemsRef.current = refetch;
    },
    []
  );

  // Open edit modal
  const handleOpenEditModal = () => {
    setIsEditModalOpen(true);
  };

  // Close edit modal
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  // Handle order selection from search
  const handleOrderSelected = (orderId: string | null) => {
    // Don't allow changing order if there are unsaved changes
    if (changedOrderItemsCount > 0) {
      showError(
        'Please save or discard your changes before selecting a different order'
      );
      return;
    }
    setSelectedOrderId(orderId);
  };

  // Handle save all changes
  const handleSaveAllChanges = async () => {
    try {
      // Get all order items with changes
      const changes = Object.values(orderItemChanges);

      if (changes.length === 0) {
        showError('No changes to save');
        return;
      }
      setIsSaving(true);

      // Ensure each change has both carrierId and originalCarrierId
      const processedChanges = changes.map((change) => {
        const originalOrderItem = originalOrderItems[change.id];

        // Use getShipmentInfo to correctly extract shipment data
        const shipmentInfo = getShipmentInfo(originalOrderItem);
        const originalCarrierId = shipmentInfo.shipmentCarrierId;
        const originalTrackingNumber = shipmentInfo.trackingNumber;

        // Need to handle slightly different logic for tracking number so we can delete
        let newTrackingNumber = change.trackingNumber;
        if (
          change.trackingNumber !== originalTrackingNumber &&
          !change.trackingNumber
        ) {
          newTrackingNumber = null;
        } else {
          newTrackingNumber = change.trackingNumber ?? originalTrackingNumber;
        }

        return {
          ...change,
          // If carrierId wasn't changed, use the original
          carrierId: change.carrierId ?? originalCarrierId,
          // Always include the original carrier ID
          originalCarrierId: change.originalCarrierId ?? originalCarrierId,

          // If tracking number wasn't changed, use the original
          trackingNumber: newTrackingNumber,
          // Always include the original tracking number
          originalTrackingNumber:
            change.originalTrackingNumber ?? originalTrackingNumber,
        };
      });

      console.log('processedChanges', processedChanges);

      // Call the REST API endpoint using MonoService
      await MonoService.post('/v1/shipping-management', {
        changes: processedChanges,
      });

      // Refetch data to update the UI with the latest information
      const updatedData = await refetchOrderItemsRef.current();

      // Clear changes from Redux store
      dispatch(clearOrderItemChanges());

      // If we have updated data and it contains order items, update the originalOrderItems in Redux
      if (updatedData?.data?.v2FindAndCountOrderItems?.orderItems) {
        dispatch(
          storeOriginalOrderItems(
            updatedData.data.v2FindAndCountOrderItems.orderItems
          )
        );
      }

      setIsSaving(false);
      showSuccess(`Successfully saved ${changes.length} changes`);
    } catch (error: any) {
      showError(
        error.message ||
          error.response?.data?.message ||
          'Failed to save changes'
      );
      setIsSaving(false);
    }
  };

  // Handle discard all changes
  const handleDiscardAllChanges = () => {
    const changedOrderItemIds = Object.keys(orderItemChanges);

    if (changedOrderItemIds.length === 0) {
      showError('No changes to discard');
      return;
    }

    // Revert to original values
    dispatch(revertToOriginal());
    showSuccess(
      `Discarded changes for ${changedOrderItemIds.length} order items`
    );
  };

  // Count the number of changed order items
  const changedOrderItemsCount = Object.keys(orderItemChanges).length;

  return (
    <Container
      data-testid={fixed.DATA_TESTID_SHIPPING_DASH}
      sx={styles.AMDashContainerSx}
    >
      <Stack
        data-testid={fixed.DATA_TESTID_SHIPPING_TOP_STACK}
        direction="column"
      >
        <ManagementDashboardToggle />
        <Spacer height="16px" />
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={styles.MainHeaderSx}>Shipping</Typography>
        </Stack>
        <Spacer height="16px" />
      </Stack>
      <Stack
        data-testid={fixed.DATA_TESTID_SHIPPING_MID_STACK}
        direction="column"
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <ShippingSearch
            onOrderSelected={handleOrderSelected}
            disabled={changedOrderItemsCount > 0}
          />
          <Stack direction="row" gap="16px">
            <Button
              variant="outlined"
              disableRipple
              color="secondary"
              sx={styles.SaveChangesButtonSx}
              startIcon={<EditIcon />}
              onClick={handleOpenEditModal}
              disabled={selectedOrderItemIds.length === 0}
            >
              <Typography variant="button">
                Edit Selected{' '}
                {selectedOrderItemIds.length > 0
                  ? `(${selectedOrderItemIds.length})`
                  : ''}
              </Typography>
            </Button>
            <Button
              variant="contained"
              disableRipple
              color="primary"
              sx={styles.SaveChangesButtonSx}
              startIcon={<CheckCircleOutlineIcon />}
              onClick={handleSaveAllChanges}
              disabled={
                changedOrderItemsCount === 0 || isSaving || loadingCarriers
              }
            >
              <Typography variant="button">
                {isSaving
                  ? 'Saving...'
                  : `Save changes${
                      changedOrderItemsCount > 0
                        ? ` (${changedOrderItemsCount})`
                        : ''
                    }`}
              </Typography>
            </Button>
            <Button
              variant="outlined"
              disableRipple
              color="secondary"
              sx={styles.SaveChangesButtonSx}
              startIcon={<UndoIcon />}
              onClick={handleDiscardAllChanges}
              disabled={changedOrderItemsCount === 0}
            >
              <Typography variant="button">Discard all changes</Typography>
            </Button>
          </Stack>
        </Stack>

        <Spacer height="16px" />
        <Stack
          data-testid={fixed.DATA_TESTID_SHIPPING_END_STACK}
          direction="column"
        >
          <ShippingTable
            setSelectedOrderItems={setSelectedOrderItems}
            selectedOrderId={selectedOrderId}
            carriers={carriers}
            loadingCarriers={loadingCarriers}
            setRefetchOrderItems={setRefetchOrderItems}
            disablePagination={changedOrderItemsCount > 0}
          />
          <Spacer height="32px" />
        </Stack>
      </Stack>

      {/* Edit Order Items Modal */}
      <EditOrderItemsModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        selectedOrderItems={selectedOrderItems}
        carriers={carriers}
        loadingCarriers={loadingCarriers}
      />
    </Container>
  );
};

export default Shipping;
